import React from "react";
import { Autocomplete, Box, Button, Chip, TextField, Typography } from "@mui/material";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { formatDate } from "../../common/constant/dates";
import {
  BroadcastResult,
  TelegramRecipient,
  getTelegramRecipients,
  sendTelegramBroadcast,
} from "../../common/requests/notifications/endpoints";
import { BroadcastHistoryEntry, getBroadcastHistory } from "../../common/requests/activity/endpoints";
import { Banner, Column, ConfirmDialog, DataTable, ErrorState, Panel, Section, StatusPill, errorMessage, useResource, useToast } from "../../ui";
import { TELEGRAM_LIMIT, deliveryTone, messageError, recipientLabel, splitRecipients } from "./broadcastData";

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

/** Send a Telegram message from the Inflos bot to chosen users, with the
 * history of what was sent. The "Messages" tab of Users & Sellers. */
export default function NotificationsBroadcast() {
  const { idToken } = useAuth();
  const toast = useToast();
  const recipients = useResource(async () => (await getTelegramRecipients(idToken!)).data, [idToken], {
    enabled: !!idToken,
    errorText: "Couldn't load Telegram recipients.",
  });
  const history = useResource(
    async () => {
      const { data } = await getBroadcastHistory(idToken!);
      return data.available ? data.broadcasts : null;
    },
    [idToken],
    { enabled: !!idToken, errorText: "Couldn't load sent messages." }
  );

  const [selected, setSelected] = React.useState<TelegramRecipient[]>([]);
  const [message, setMessage] = React.useState("");
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<BroadcastResult | null>(null);

  const all = React.useMemo(() => recipients.data ?? [], [recipients.data]);
  const { sellers, buyers } = React.useMemo(() => splitRecipients(all), [all]);
  const trimmed = message.trim();
  const tooLong = trimmed.length > TELEGRAM_LIMIT;
  const blocker = messageError(selected.length, trimmed);

  const send = async () => {
    if (!idToken) return;
    setSending(true);
    setSendError(null);
    try {
      const { data } = await sendTelegramBroadcast(
        selected.map((u) => u.user_id),
        trimmed,
        idToken
      );
      setResult(data);
      setConfirmOpen(false);
      history.reload();
      if (data.failed.length === 0) {
        toast(`Sent to ${data.sent} of ${data.requested}`);
        setMessage("");
        setSelected([]);
      } else {
        // Keep only the people it didn't reach selected, so a retry can't double-send.
        const failedIds = new Set(data.failed.map((f) => f.user_id));
        setSelected((prev) => prev.filter((u) => failedIds.has(u.user_id)));
        toast(`Sent to ${data.sent} of ${data.requested}; ${data.failed.length} failed`, "error");
      }
    } catch (err) {
      setSendError(errorMessage(err, "Sending failed. Nothing was confirmed as sent."));
    } finally {
      setSending(false);
    }
  };

  const historyColumns: Column<BroadcastHistoryEntry>[] = [
    {
      key: "message",
      header: "Message",
      card: "title",
      render: (h) => (
        <Typography
          variant="body2"
          title={h.message ?? ""}
          sx={{ fontWeight: "inherit", whiteSpace: "pre-wrap", overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
        >
          {h.message || "–"}
        </Typography>
      ),
    },
    {
      key: "sent_at",
      header: "Sent",
      card: "subtitle",
      width: 150,
      sortValue: (h) => new Date(h.sent_at).getTime(),
      render: (h) => (
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          {formatDate(h.sent_at)} {timeOf(h.sent_at)}
        </Box>
      ),
    },
    { key: "by", header: "By", card: "subtitle", hideBelow: 1000, render: (h) => h.sent_by || "–" },
    {
      key: "delivered",
      header: "Delivered",
      card: "aside",
      align: "right",
      width: 130,
      render: (h) => (
        <StatusPill tone={deliveryTone(h)}>
          {h.sent ?? 0} of {h.requested ?? 0}
          {h.failed ? ` · ${h.failed} failed` : ""}
        </StatusPill>
      ),
    },
  ];

  const quick = (label: string, list: TelegramRecipient[]) => (
    <Chip label={`${label} (${list.length})`} onClick={() => setSelected(list)} disabled={!list.length} sx={{ minHeight: 36 }} />
  );

  return (
    <>
      <Section title="New message" subtitle="Sent from the Inflos Telegram bot. Messages can't be recalled once sent.">
        <Panel sx={{ maxWidth: { xs: "100%", md: 760 }, display: "flex", flexDirection: "column", gap: 2 }}>
          {recipients.status === "error" && !recipients.data ? (
            <ErrorState message={recipients.error} onRetry={recipients.reload} />
          ) : (
            <>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <Typography variant="overline" sx={{ color: C.muted, mr: 0.5 }}>
                  Quick select
                </Typography>
                {quick("All", all)}
                {quick("Sellers", sellers)}
                {quick("Buyers", buyers)}
                {selected.length > 0 && <Chip variant="outlined" label="Clear" onClick={() => setSelected([])} sx={{ minHeight: 36 }} />}
              </Box>
              <Autocomplete
                multiple
                options={all}
                loading={recipients.status === "loading" || recipients.status === "idle"}
                disableCloseOnSelect
                limitTags={6}
                getOptionLabel={recipientLabel}
                isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  return q ? options.filter((o) => `${o.username ?? ""} ${o.email ?? ""}`.toLowerCase().includes(q)) : options;
                }}
                renderOption={(props, option) => {
                  const { key, ...rest } = props as typeof props & { key: string };
                  return (
                    <li key={key} {...rest}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {recipientLabel(option)}
                          <Typography component="span" variant="caption" sx={{ color: C.muted }}>
                            {" "}
                            · {option.is_seller ? "seller" : "buyer"}
                          </Typography>
                        </Typography>
                        {option.email && option.username && (
                          <Typography variant="caption" noWrap component="div" sx={{ color: C.muted }}>
                            {option.email}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                value={selected}
                onChange={(_, value) => setSelected(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Recipients (${selected.length})`}
                    placeholder="Search username or email"
                    helperText="Only users who have linked the Inflos Telegram bot are listed."
                  />
                )}
              />
              <TextField
                label="Message"
                multiline
                minRows={5}
                fullWidth
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                error={tooLong}
                helperText={`${trimmed.length} / ${TELEGRAM_LIMIT} characters`}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                {blocker && (
                  <Typography variant="caption" sx={{ color: C.muted, flex: "1 1 200px" }}>
                    {blocker}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  onClick={() => {
                    setSendError(null);
                    setConfirmOpen(true);
                  }}
                  disabled={!!blocker}
                  sx={{ minHeight: 44, width: { xs: "100%", sm: "auto" } }}
                >
                  Review and send
                </Button>
              </Box>
            </>
          )}
        </Panel>

        {result && result.failed.length > 0 && (
          <Banner tone="warning" title={`Sent to ${result.sent} of ${result.requested}.`} action={<Button onClick={() => setResult(null)}>Dismiss</Button>}>
            Not delivered to{" "}
            {result.failed.map((f, i) => (
              <Box component="span" key={f.user_id} sx={{ display: "block", overflowWrap: "anywhere" }}>
                {f.username || f.user_id}: {f.error}
                {i === result.failed.length - 1 ? "" : ";"}
              </Box>
            ))}
            They're still selected if you want to retry.
          </Banner>
        )}
      </Section>

      {history.data !== null || history.status !== "ready" ? (
        <Section title="Sent messages">
          <DataTable
            aria-label="Sent messages"
            rows={history.data ?? []}
            columns={historyColumns}
            getRowId={(h) => h.id}
            loading={history.status === "loading" || history.status === "idle"}
            error={history.error}
            onRetry={history.reload}
            initialSort={{ key: "sent_at", dir: "desc" }}
            empty="No messages sent yet."
          />
        </Section>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title="Send this Telegram message?"
        confirmLabel={`Send to ${selected.length}`}
        busy={sending}
        error={sendError}
        onConfirm={send}
        onClose={() => setConfirmOpen(false)}
        body={
          <>
            <Typography variant="body2">
              To <b>{selected.length}</b> user{selected.length === 1 ? "" : "s"} from the Inflos bot. Messages can't be recalled once sent.
            </Typography>
            <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: C.ground, border: `1px solid ${C.line}`, whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 14, maxHeight: 320, overflowY: "auto" }}>
              {trimmed}
            </Box>
          </>
        }
      />
    </>
  );
}
